import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate, faPlane, faPersonWalkingLuggage,
  faBed, faPersonWalking, faCheckCircle, faUser,
  faCalendarAlt, faBed as faBedSolid,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "./GuestJourneyPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface Booking {
  bookingid: string;
  guestname: string;
  roomNumber: string;
  checkin: string;
  checkout: string;
  nights: number;
  phone: string;
  total: number;
  status: string;
  bookingType?: string;
}

interface Column {
  key: string;
  label: string;
  icon: any;
  color: string;
  bg: string;
  border: string;
}

const COLUMNS: Column[] = [
  { key: "pre_arrival", label: "Pre-Arrival",         icon: faPlane,                  color: "#1a3a5c", bg: "rgba(14,34,64,0.10)",  border: "rgba(14,34,64,0.10)"  },
  { key: "arriving",    label: "Arriving Today",       icon: faPersonWalkingLuggage,   color: "#34d399", bg: "rgba(52,211,153,0.08)",  border: "rgba(52,211,153,0.2)"  },
  { key: "in_house",    label: "In House",             icon: faBed,                    color: "#fbbf24", bg: "rgba(251,191,36,0.08)",  border: "rgba(251,191,36,0.2)"  },
  { key: "departing",   label: "Checking Out Today",   icon: faPersonWalking,          color: "#fb923c", bg: "rgba(249,115,22,0.08)",  border: "rgba(249,115,22,0.2)"  },
  { key: "departed",    label: "Departed",             icon: faCheckCircle,            color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.06)", border: "rgba(100,116,139,0.15)" },
];

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function today() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`;
}

function localDatePlus(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
}

export default function GuestJourneyPage() {
  const [columns, setColumns] = useState<Record<string, Booking[]>>({
    pre_arrival: [], arriving: [], in_house: [], departing: [], departed: [],
  });
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState("");
  const [search, setSearch]   = useState("");

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const td = today();
      const [notifRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/notifications`),
        axios.get(`${API}/api/bookings`),
      ]);

      const notif = notifRes.data;
      const allBookings: any[] = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];

      const normalize = (b: any, roomNum?: string): Booking => ({
        bookingid: b.bookingId || b.bookingid || "",
        guestname: b.guestName || b.guestname || "—",
        roomNumber: b.roomNumber || roomNum || b.roomid || "—",
        checkin: b.checkIn || b.checkin || "",
        checkout: b.checkOut || b.checkout || "",
        nights: b.nights || 0,
        phone: b.phone || b.phonenumber || "",
        total: b.total || 0,
        status: b.status || "",
        bookingType: b.bookingType || b.booking_type || "Standard",
      });

      // Build sets of IDs that appear in notif
      const arrivingIds = new Set((notif.todayArrivals || []).map((b: any) => b.bookingId || b.id));
      const departingIds = new Set((notif.todayDepartures || []).map((b: any) => b.bookingId || b.id));

      // Filter full bookings list
      const arriving: Booking[] = (notif.todayArrivals || []).map((b: any) => normalize(b));
      const departing: Booking[] = (notif.todayDepartures || []).map((b: any) => normalize(b));

      const in_house: Booking[] = allBookings
        .filter(b => {
          const id = b.bookingid || b.bookingId || "";
          if (arrivingIds.has(id) || departingIds.has(id)) return false;
          if (b.status === "cancelled" || b.status === "deleted") return false;
          const cin  = b.checkin || b.checkIn || "";
          const cout = b.checkout || b.checkOut || "";
          return cin <= td && cout > td;
        })
        .map(b => normalize(b));

      // Pre-arrival: only next 5 days (not all future bookings)
      const in5days = localDatePlus(td, 5);
      const pre_arrival: Booking[] = allBookings
        .filter(b => {
          if (b.status === "cancelled" || b.status === "deleted") return false;
          const cin = b.checkin || b.checkIn || "";
          return cin > td && cin <= in5days;
        })
        .sort((a, b) => (a.checkin || "").localeCompare(b.checkin || ""))
        .map(b => normalize(b));

      // Departed: only guests who checked out TODAY
      const departed: Booking[] = allBookings
        .filter(b => {
          const cout = b.checkout || b.checkOut || "";
          return cout === td && (b.status === "checked-out" || b.status === "completed");
        })
        .map(b => normalize(b));

      setColumns({ pre_arrival, arriving, in_house, departing, departed });
    } catch {
      showToast("Failed to load guest journey data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = search.toLowerCase();
  const filtered: Record<string, Booking[]> = {};
  for (const key of Object.keys(columns)) {
    filtered[key] = q
      ? columns[key].filter(b =>
          b.guestname.toLowerCase().includes(q) ||
          String(b.roomNumber).toLowerCase().includes(q) ||
          b.bookingid.toLowerCase().includes(q)
        )
      : columns[key];
  }

  const totalGuests = Object.values(columns).reduce((s, arr) => s + arr.length, 0);

  return (
    <DashboardLayout>
      <div className="gj-page">
        {/* Header */}
        <div className="gj-header">
          <div>
            <h1 className="gj-title">
              <FontAwesomeIcon icon={faBedSolid} /> Guest Journey Board
            </h1>
            <p className="gj-subtitle">
              Live Kanban of all guests — {totalGuests} total
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input
              className="gj-search"
              placeholder="Search guest, room…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button className="gj-refresh-btn" onClick={load}>
              <FontAwesomeIcon icon={faArrowsRotate} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="gj-loading">Loading guest journey data…</div>
        ) : (
          <div className="gj-board">
            {COLUMNS.map(col => {
              const cards = filtered[col.key] || [];
              return (
                <div
                  key={col.key}
                  className="gj-column"
                  style={{ borderTop: `3px solid ${col.color}` }}
                >
                  {/* Column header */}
                  <div className="gj-col-header">
                    <span className="gj-col-icon" style={{ color: col.color }}>
                      <FontAwesomeIcon icon={col.icon} />
                    </span>
                    <span className="gj-col-label">{col.label}</span>
                    <span
                      className="gj-col-count"
                      style={{ background: col.bg, color: col.color, border: `1px solid ${col.border}` }}
                    >
                      {cards.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="gj-card-list">
                    {cards.length === 0 && (
                      <div className="gj-col-empty">No guests</div>
                    )}
                    {cards.map((b, i) => (
                      <div
                        key={`${b.bookingid}-${i}`}
                        className="gj-card"
                        style={{ borderLeft: `3px solid ${col.color}40` }}
                      >
                        <div className="gj-card-top">
                          <div className="gj-avatar" style={{ background: col.bg, color: col.color }}>
                            {b.guestname?.[0] || "?"}
                          </div>
                          <div className="gj-card-info">
                            <div className="gj-guest-name">{b.guestname}</div>
                            <div className="gj-card-room">
                              <FontAwesomeIcon icon={faBedSolid} /> Room {b.roomNumber}
                            </div>
                          </div>
                        </div>
                        <div className="gj-card-dates">
                          <span><FontAwesomeIcon icon={faCalendarAlt} /> {fmt(b.checkin)} → {fmt(b.checkout)}</span>
                          <span className="gj-nights">{b.nights}n</span>
                        </div>
                        {b.bookingType && b.bookingType !== "Standard" && (
                          <span className="gj-type-tag">{b.bookingType}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {toast && <div className="gj-toast">{toast}</div>}
      </div>
    </DashboardLayout>
  );
}
